/**
 * Range and tree surgery shared by every command.
 *
 * Nothing here calls `document.execCommand`. Each helper keeps live node
 * references valid where it can, because the commands restore the selection
 * from those references rather than from a recomputed offset.
 */

import { BLOCK_TAGS, LIST_TAGS, MARK_TAGS } from './schema';

/** Nesting order we canonicalize to, so the serialized string stays stable. */
const MARK_RANK: ReadonlyMap<string, number> = new Map([['A', 0], ['B', 1], ['I', 2]]);

export function isElement(node: Node | null): node is Element {
  return !!node && node.nodeType === Node.ELEMENT_NODE;
}

export function isText(node: Node | null): node is Text {
  return !!node && node.nodeType === Node.TEXT_NODE;
}

/** Nearest ancestor (inclusive) matching `predicate`, bounded by `root`. */
export function closestWithin(
  node: Node | null,
  root: HTMLElement,
  predicate: (element: Element) => boolean,
): Element | null {
  let current: Node | null = node;
  while (current && current !== root) {
    if (isElement(current) && predicate(current)) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
}

/** The `<p>` or `<li>` that owns `node`. */
export function blockOf(node: Node | null, root: HTMLElement): Element | null {
  return closestWithin(node, root, (element) => element.nodeName === 'P' || element.nodeName === 'LI');
}

/** The mark ancestor of the given tag, if the node sits inside one. */
export function markAncestor(node: Node | null, root: HTMLElement, tag: string): Element | null {
  return closestWithin(node, root, (element) => element.nodeName === tag);
}

/**
 * Splits `text` at `offset` and returns the node holding the right-hand half.
 * Both halves stay live, so callers can keep either reference.
 */
export function splitTextAt(text: Text, offset: number): Text {
  if (offset <= 0) {
    return text;
  }
  if (offset >= text.data.length) {
    const empty = document.createTextNode('');
    text.after(empty);
    return empty;
  }
  return text.splitText(offset);
}

/**
 * Splits `parent` immediately before `at`, moving `at` and everything after it
 * into a fresh clone inserted after `parent`. Returns the clone.
 */
export function splitElementAt(parent: Element, at: Node): Element {
  const clone = parent.cloneNode(false) as Element;
  let moving: Node | null = at;
  while (moving) {
    const next: Node | null = moving.nextSibling;
    clone.appendChild(moving);
    moving = next;
  }
  parent.after(clone);
  return clone;
}

/** Replaces an element with its children, returning them. */
export function unwrap(element: Element): Node[] {
  const children = Array.from(element.childNodes);
  const parent = element.parentNode;
  if (!parent) {
    return children;
  }
  for (const child of children) {
    parent.insertBefore(child, element);
  }
  parent.removeChild(element);
  return children;
}

/**
 * Restructures the tree so that `ancestor` ends up containing nothing but the
 * path down to `node`, splitting every level in between, and returns that
 * isolated copy of `ancestor`.
 *
 * This is what makes "unbold just the selected part" work: isolate the `<b>`
 * around the selected text, then unwrap only that copy, leaving the untouched
 * halves bold and any other marks on the path intact.
 */
export function isolate(node: Node, ancestor: Element): Element {
  const stopAt = ancestor.parentNode;
  let current: Node = node;

  while (current.parentNode && current.parentNode !== stopAt) {
    const parent = current.parentNode as Element;

    if (current.nextSibling) {
      splitElementAt(parent, current.nextSibling);
    }

    if (current.previousSibling) {
      // `current` (now the last child) moves into a fresh copy of `parent`.
      current = splitElementAt(parent, current);
    } else {
      current = parent;
    }
  }

  return current as Element;
}

/** Every text node that the range touches, in document order. */
export function textLeavesInRange(root: HTMLElement, range: Range): Text[] {
  const leaves: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const text = node as Text;
    if (text.data.length > 0 && range.intersectsNode(text)) {
      const nodeRange = document.createRange();
      nodeRange.selectNodeContents(text);
      const startsBeforeEnd = range.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0;
      const endsAfterStart = range.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0;
      if (startsBeforeEnd && endsAfterStart) {
        leaves.push(text);
      }
    }
    node = walker.nextNode();
  }
  return leaves;
}

/** The `<p>`/`<li>` blocks the range touches, in document order. */
export function blocksInRange(root: HTMLElement, range: Range): Element[] {
  const blocks: Element[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) =>
      node.nodeName === 'P' || node.nodeName === 'LI'
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP,
  });
  let node = walker.nextNode();
  while (node) {
    if (range.intersectsNode(node)) {
      blocks.push(node as Element);
    }
    node = walker.nextNode();
  }
  return blocks;
}

export function isEmptyBlock(block: Element): boolean {
  return (block.textContent ?? '').length === 0;
}

/** A block with no content collapses to zero height; give it a `<br>` to hold. */
export function ensureFillerBr(block: Element): void {
  if (block.childNodes.length === 0) {
    block.appendChild(document.createElement('br'));
  }
}

function sameMark(a: Node, b: Node): boolean {
  if (!isElement(a) || !isElement(b) || a.nodeName !== b.nodeName || !MARK_TAGS.has(a.nodeName)) {
    return false;
  }
  if (a.attributes.length !== b.attributes.length) {
    return false;
  }
  return Array.from(a.attributes).every((attr) => b.getAttribute(attr.name) === attr.value);
}

/**
 * Canonicalizes `<b><a>` to `<a><b>` and so on, so that two documents holding
 * the same formatting always serialize to the same string. That matters because
 * the serialized string drives a debounced autosave: an unstable serialization
 * would fire spurious network writes.
 */
function canonicalizeNesting(block: Element): void {
  let changed = true;
  let guard = 0;
  while (changed && guard < 8) {
    changed = false;
    guard += 1;
    const marks = Array.from(block.querySelectorAll('b, i, a'));
    for (const outer of marks) {
      if (outer.childNodes.length !== 1) {
        continue;
      }
      const inner = outer.firstChild;
      if (!isElement(inner) || !MARK_TAGS.has(inner.nodeName)) {
        continue;
      }
      const outerRank = MARK_RANK.get(outer.nodeName) ?? 99;
      const innerRank = MARK_RANK.get(inner.nodeName) ?? 99;
      if (innerRank >= outerRank) {
        continue;
      }
      // Swap: the lower-ranked mark belongs on the outside.
      const swapped = inner.cloneNode(false) as Element;
      const host = outer.cloneNode(false) as Element;
      while (inner.firstChild) {
        host.appendChild(inner.firstChild);
      }
      swapped.appendChild(host);
      outer.replaceWith(swapped);
      changed = true;
    }
  }
}

/** Drops empty marks, merges identical neighbours and joins split text nodes. */
export function mergeInlines(block: Element): void {
  for (const mark of Array.from(block.querySelectorAll('b, i, a'))) {
    if ((mark.textContent ?? '').length === 0 && !mark.querySelector('br')) {
      mark.remove();
    }
  }

  canonicalizeNesting(block);

  let child = block.firstChild;
  while (child) {
    const next = child.nextSibling;
    if (next && sameMark(child, next)) {
      const target = child as Element;
      while (next.firstChild) {
        target.appendChild(next.firstChild);
      }
      next.parentNode?.removeChild(next);
      continue; // re-check the same child against its new neighbour
    }
    if (isElement(child)) {
      mergeInlines(child);
    }
    child = next;
  }

  block.normalize();
  ensureFillerBr(block);
}

/** True when the node is a list container. */
export function isList(node: Node | null): node is Element {
  return isElement(node) && LIST_TAGS.has(node.nodeName);
}

/** True when the node is one of our block containers. */
export function isBlock(node: Node | null): node is Element {
  return isElement(node) && BLOCK_TAGS.has(node.nodeName);
}

/**
 * Splits the text nodes at the range boundaries so the selection lines up with
 * whole nodes, and returns the leaves the range now covers exactly.
 *
 * The end boundary is split before the start one: when both boundaries sit in
 * the same text node, splitting the start first would invalidate the end
 * offset. This ordering is the fix for "bold only applied to half the word".
 */
export function splitRangeBoundaries(root: HTMLElement, range: Range): Text[] {
  const leaves = textLeavesInRange(root, range);
  if (leaves.length === 0) {
    return [];
  }

  const first = leaves[0]!;
  let last = leaves[leaves.length - 1]!;

  if (last === range.endContainer && range.endOffset < last.data.length) {
    splitTextAt(last, range.endOffset);
  }

  if (first === range.startContainer && range.startOffset > 0) {
    const right = splitTextAt(first, range.startOffset);
    if (first === last) {
      last = right;
      return [right];
    }
    leaves[0] = right;
  }

  return leaves;
}
